import { ConfigProvider, Layout, Menu, theme, type MenuProps } from 'antd';
import { FC, memo, useMemo } from 'react';
import {
    Cloud,
    Diagram3Fill,
    House,
    LayersFill,
    MenuButtonWideFill,
    PcDisplay,
    Postcard,
    WrenchAdjustableCircle
} from 'react-bootstrap-icons';
import { Link, useLocation } from 'react-router-dom';

import { DomainIcon, SiteIcon } from '@/common/components';
import { AppRoutes } from '@/common/interfaces';

import { SiderMenuItems } from './sider.interfaces';

import './Sider.scss';

export const Sider: FC = memo(() => {
  const location = useLocation();
  const { colorBorder } = theme.useToken().token;

  const menuItems: NonNullable<MenuProps['items']> = [
    {
      key: AppRoutes.Home,
      icon: <House/>,
      label: <Link to={ AppRoutes.Home }>{ SiderMenuItems.Home }</Link>
    },
    {
      key: AppRoutes.Sites,
      icon: <SiteIcon/>,
      label: <Link to={ AppRoutes.Sites }>{ SiderMenuItems.Sites }</Link>
    },
    {
      key: AppRoutes.SiteGroups,
      icon: <LayersFill/>,
      label: <Link to={ AppRoutes.SiteGroups }>{ SiderMenuItems.SiteGroups }</Link>
    },
    {
      key: AppRoutes.Domains,
      icon: <DomainIcon/>,
      label: <Link to={ AppRoutes.Domains }>{ SiderMenuItems.Domains }</Link>
    },
    {
      key: AppRoutes.DomainRegistrars,
      icon: <Postcard/>,
      label: <Link to={ AppRoutes.DomainRegistrars }>{ SiderMenuItems.DomainRegistrars }</Link>
    },
    {
      key: AppRoutes.Hostings,
      icon: <MenuButtonWideFill/>,
      label: <Link to={ AppRoutes.Hostings }>{ SiderMenuItems.Hostings }</Link>
    },
    {
      key: AppRoutes.ProxyServers,
      icon: <PcDisplay/>,
      label: <Link to={ AppRoutes.ProxyServers }>{ SiderMenuItems.ProxyServers }</Link>
    },
    {
      key: AppRoutes.DnsRecords,
      icon: <Diagram3Fill/>,
      label: <Link to={ AppRoutes.DnsRecords }>{ SiderMenuItems.DnsRecords }</Link>
    },
    {
      key: AppRoutes.DomainsApi,
      type: 'group',
      label: SiderMenuItems.DomainsApi,
      children: [
        {
          key: AppRoutes.DomainsApiCloudflare,
          icon: <Cloud/>,
          label: <Link to={ AppRoutes.DomainsApiCloudflareAccounts }>{ SiderMenuItems.DomainsApiCloudflare }</Link>
        },
        {
          key: AppRoutes.DomainsApiSearchConsole,
          icon: <WrenchAdjustableCircle/>,
          label: <Link to={ AppRoutes.DomainsApiSearchConsoleAccounts }>{ SiderMenuItems.DomainsApiSearchConsole }</Link>
        }
      ]
    }
  ];

  const currentMenuItem = useMemo<string[]>(
    () => [
      location.pathname === '/'
        ? AppRoutes.Home
        : menuItems
          .flatMap((item) => (item && 'children' in item ? item.children : item))
          .filter((item) => item?.key !== AppRoutes.Home)
          .find((item) =>
            location.pathname.split('/').filter(Boolean).length > 1
              ? location.pathname.startsWith(item?.key?.toString() + '/')
              : location.pathname.startsWith(item?.key?.toString() ?? '/')
          )?.key?.toString() ?? AppRoutes.Home
    ],
    [location.pathname]
  );

  return (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: '#003eb3'
        }
      }}
    >
      <Layout.Sider
        width={ '100%' }
        className="app-sider"
        collapsed={ false }
        style={{ borderRight: `1px solid ${ colorBorder }` }}
      >
        <div className="brand-badge">
          <span>Workground</span>
        </div>

        <Menu
          mode="inline"
          theme="light"
          defaultSelectedKeys={ currentMenuItem }
          selectedKeys={ currentMenuItem }
          items={ menuItems }
        />
      </Layout.Sider>
    </ConfigProvider>
  );
});
